import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchResultsProps } from '../../../types/search';
import { Content } from '../../../types/content';
import { Topic } from '../../../types/topic';
import ResultRenderer from './ResultRenderer';
import { useSearchContext } from '../../../contexts/SearchContext';
import { LoadingSpinner } from '../../common';
import ErrorBoundary from '../../common/ErrorBoundary/ErrorBoundary';
import { useGridLayout } from '../../../hooks/useGridLayout';
import { usePagination } from '../../../hooks/usePagination';
import { useResponsiveView } from '../../../hooks/useResponsiveView';
import Pagination from '../../common/Pagination';
import { SEARCH_RESULT_LIMIT } from '../../../constants/search';
import { HttpError } from '../../../services/api/client/httpClient';
import { getUserFriendlyError } from '../../../utils/errorMapping';

type SearchResultItem = Content | Topic;

type SearchError = HttpError | Error | string | null;

interface ExtendedSearchResultsProps extends Omit<SearchResultsProps<SearchResultItem>, 'error'> {
  error: SearchError;
  term?: string;
}

const SearchResultsContent: React.FC<ExtendedSearchResultsProps> = ({ 
  results, 
  loading, 
  error, 
  hasMore, 
  onLoadMore,
  total = 0,
  page = 1,
  onPageChange,
  term = ''
}) => {
  const { t } = useTranslation();
  const { contentType } = useSearchContext();
  const { isMobile } = useResponsiveView();
  const gridLayout = useGridLayout(contentType === 'shorts' ? 'shorts' : 'regular');
  const sentinel = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMobile || !sentinel.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          onLoadMore?.();
        }
      },
      { rootMargin: '50px' }
    );

    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [isMobile, hasMore, loading, onLoadMore]);

  const renderLoading = () => (
    <div className="flex justify-center items-center py-12">
      <LoadingSpinner />
    </div>
  );

  const renderEmptyState = () => (
    <div className="text-center py-12">
      <p className="text-text-secondary">{t('search.noResults', { query: term })}</p>
    </div>
  );

  if (loading && !results.length) {
    return renderLoading();
  }

  if (error) {
    // Convert any error to a normalized error message
    const errorMessage = getUserFriendlyError(error, t, error instanceof HttpError ? 'internal' : 'external');
    throw new Error(errorMessage);
  }

  if (!results.length) {
    return renderEmptyState();
  }

  const contentResults = results.filter((item): item is Content => 'type' in item);
  const topicResults = results.filter((item): item is Topic => !('type' in item));

  return (
    <div ref={resultsRef} id="search-results" className="space-y-14">
      {contentType !== 'topics' && contentResults.length > 0 && (
        <div className={gridLayout.containerClass}>
          <div className={`${gridLayout.gridClass} gap-x-4 gap-y-8`} style={gridLayout.gridStyle}>
            {contentResults.map((item, index) => (
              <div 
                key={`${item.id}-${index}`} 
                className={gridLayout.itemClass}
              >
                <ResultRenderer
                  item={item}
                  thumbnailClass={gridLayout.thumbnailClass}
                  thumbnailContentClass={gridLayout.thumbnailContentClass}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {contentType === 'topics' && topicResults.length > 0 && (
        <div className={gridLayout.containerClass}>
          <div className={`${gridLayout.gridClass} gap-x-4 gap-y-8`} style={gridLayout.gridStyle}>
            {topicResults.map((item, index) => (
              <div 
                key={`${item.id}-${index}`} 
                className={gridLayout.itemClass}
              >
                <ResultRenderer
                  item={item}
                  thumbnailClass={gridLayout.thumbnailClass}
                  thumbnailContentClass={gridLayout.thumbnailContentClass}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {isMobile && hasMore && (
        <div ref={sentinel} className="h-8" />
      )}

      {!isMobile && total > SEARCH_RESULT_LIMIT && (
        <div className="mt-12 flex justify-center">
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(total / SEARCH_RESULT_LIMIT)}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export const SearchResults: React.FC<ExtendedSearchResultsProps> = (props) => {
  return (
    <ErrorBoundary>
      <SearchResultsContent {...props} />
    </ErrorBoundary>
  );
};

export default SearchResults; 